import { Promise as BluebirdPromise } from 'bluebird';
import cns from 'classnames';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Component, Fragment, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter, Prompt } from 'react-router-dom';
import FullPageLoadingIndicator from '../../shared/components/FullPageLoadingIndicator';
import { LANGUAGE_LOCALE_LOOKUP, ROUTE_PREFIXES, SUPPORTED_LANGUAGES } from '../../shared/Constants';
import { applyWhiteLabelStyle } from '../../shared/utils/StylingUtils';
import { isIE } from '../../shared/utils/WindowUtils';

import '../../sass/main.scss';

import Admin from '../../admin/containers/Admin';
import AdminNavigation from '../../admin/containers/Navigation/AdminNavigation';
import DesignPage from '../../admin/components/DesignPage';
import AuthenticatedRoute from '../../shared/components/AuthenticatedRoute';
import NotFound from '../../shared/components/NotFound';
import VerbRoute from '../../shared/components/VerbRoute';
import GenericErrorPage from '../../shared/containers/GenericErrorPage';
import AssetLink from '../components/AssetLink';
import AuthCallback from '../components/AuthCallback';
import BrowserSupportWarning from '../components/BrowserSupportWarning';
import CookieBanner from '../components/CookieBanner';
import DirectLink from '../components/DirectLink';
import NotificationCallback from '../components/NotificationCallback';
import AdminReauthenticationCallback from '../components/routing/AdminReauthenticationCallback';
import SilentCallback from '../components/SilentCallback';
import CollectionInterstitial from './CollectionInterstitial/CollectionInterstitial';
import CollectionPage from './CollectionPage';
import ContentCatalog from './ContentCatalog/ContentCatalog';
import DigestCallback from './DigestCallback/DigestCallback';
import DimensionPage from './Dimensions/DimensionPage';
import GroupList from './Groups/GroupList';
import CustomDiscover from './home/CustomDiscover';
import Home from './home/Home';
import LearningAssetInterstitial from './LearningAssetInterstitial/LearningAssetInterstitial';
import TakeActionActivityInterstitial from './LearningAssetInterstitial/TakeActionActivityInterstitial';
import MobileNavigation from './Navigation/MobileNavigation';
import Navigation from './Navigation/Navigation';
import PathwayInterstitial from './PathwayInterstitial/PathwayInterstitial';
import { RootIntl } from './RootIntl';
import Unsubscribe from './Unsubscribe';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';

/**
 * Widgets and Modals
 */
import {
    changeLanguage,
    collapseNavigation,
    expandNavigation,
    getCompany,
    getCompanyByPermalink,
    getManyBadges,
    getManySettings,
    getProfile,
    isMobile,
    showModal,
    updatePreviousLocation,
    setToken,
    loginUserSuccess,
    authenticateLoggedInUserOnly,
    setUnsavedChanges
} from '../../shared/actions';
import { getLocalStorage, setLocalStorage } from '../../shared/actions/LocalStorage';
import { getManyTeams } from '../../shared/actions/Teams';
import Modal from '../../shared/containers/BaseModal';
import { RuntimeContext } from '../../../common/RuntimeContext';
import { AuthenticatedUserContextConsumer } from '../../../common/AuthenticatedUser';


/**
 *
 * Lazy loaded components
 */
const Workspace = lazy(() => import('../../shared/containers/Workspace/Workspace'));
const LearningSampler = lazy(() => import('../../shared/containers/Workspace/LearningSampler'));
const Library = lazy(() => import('./Library/Library'));
const PathsTeamsContainerV2 = lazy(() => import('./Teams/PathsTeamsContainerV2'));
const Profile = lazy(() => import('./Profile/Profile'));

class App extends Component {
    static propTypes = {
        getProfile: PropTypes.func.isRequired,
        getManyTeams: PropTypes.func.isRequired,
        getCompany: PropTypes.func.isRequired,
        getManyBadges: PropTypes.func.isRequired,
        isAuthenticated: PropTypes.bool.isRequired,
        isVerbAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        isContentAuthor: PropTypes.bool,
        isContentReviewer: PropTypes.bool,
        isCompanyReportViewer: PropTypes.bool,
        isVerbReportViewer: PropTypes.bool,
        isInstructionalAdmin: PropTypes.bool,
        isInstructionalContentAdmin: PropTypes.bool,
        isInstructionalReportViewer: PropTypes.bool,
        loggedInUser: PropTypes.object,
        loggedInUserCompanyId: PropTypes.string,
        loggedInUserId: PropTypes.string,
        userCompany: PropTypes.object,
        modal: PropTypes.object.isRequired,
        mobile: PropTypes.object,
        isMobile: PropTypes.func.isRequired,
        store: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        shouldExpandNavigation: PropTypes.bool,
        expandNavigation: PropTypes.func,
        collapseNavigation: PropTypes.func,
        badges: PropTypes.object,
        changeLanguage: PropTypes.func,
        displayLanguage: PropTypes.string,
        loggedInUserLanguage: PropTypes.string,
        updatePreviousLocation: PropTypes.func,
        getManySettings: PropTypes.func,
        isContentAdmin: PropTypes.bool,
        isUpdatingWelcomeLanguage: PropTypes.bool,
        languageLoaded: PropTypes.bool,
        currentLoginPageCompany: PropTypes.object,
        teams: PropTypes.object,
        activeUserTeams: PropTypes.array,
        getCompanyByPermalink: PropTypes.func
    };
    static contextType = RuntimeContext;

    constructor(props, context) {
        super(props, context);
        props.isMobile();
        this.state = {
            userDataLoaded: false,
            missingProfileError: false,
            confirmationModalOpen: false,
        };
        this.dataFetched = false;
        this.nextPath = '';
    }

    componentDidMount() {
        const { loggedInUserId, loggedInUserCompanyId, authContext = {}, setToken, history } = this.props;
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        const promises = [];
        setToken(authContext.token);
        const { pathname: locationPathname, search } = this.props.location;
        const queryParams = queryString.parse(search);
        let searchParam = '';
        if (queryParams.path && locationPathname.match(/workspace/)) {
            searchParam += `${
                searchParam && searchParam.includes('?') ? '&' : '?'
            }path=${queryParams.path}`;
        }
        if (queryParams.takeActionReroute && locationPathname.match(/workspace/)) {
            searchParam += `${
                searchParam && searchParam.includes('?') ? '&' : '?'
            }takeActionReroute=${queryParams.takeActionReroute}`;
        }
        if (queryParams.dimension && locationPathname.match(/explore/)) {
            searchParam += `${
                searchParam && searchParam.includes('?') ? '&' : '?'
            }dimension=${queryParams.dimension}`;
        }
        this.nextPath = locationPathname ? `${locationPathname}${searchParam}`: '';
        if (authContext.token && (authContext?.companyId || loggedInUserCompanyId)) {
            this.dataFetched = true;
            if (!loggedInUserId && this.nextPath && !this.nextPath.includes('auth/callback')) {
                this.nextPath = locationPathname ? `${locationPathname}${searchParam}`: '';
                history.push(`/auth/callback`);
            }

            authenticateLoggedInUserOnly();
            promises.push(this.getLoggedInUserData(loggedInUserId, authContext?.companyId || loggedInUserCompanyId));
        }

        promises.push(this.props.isMobile());
        return BluebirdPromise.all(promises);
    }

    componentDidUpdate(prevProps, prevState) {
        const { authContext = {}, loggedInUserCompanyId, loggedInUserId, setToken } = this.props;
        
        if (
            !this.state.userDataLoaded &&
            authContext.token &&
            !this.dataFetched &&
            loggedInUserId &&
            (authContext?.companyId || loggedInUserCompanyId)
        ) {
            authenticateLoggedInUserOnly();
            this.dataFetched = true;
            this.getLoggedInUserData(loggedInUserId, authContext.companyId || loggedInUserCompanyId);
        }

        const segmentLastIdentifyDate = getLocalStorage({ key: 'segmentLastIdentifyDate', dispatchDisabled: true });
        const currentDateTime = new Date();
        const dateTime30MinAgo = new Date(currentDateTime.getTime() - 30 * 60000);

        if (
            this.props.isAuthenticated &&
            this.props.loggedInUserId &&
            segmentLastIdentifyDate &&
            segmentLastIdentifyDate < dateTime30MinAgo
        ) {
            this._identifySegmentUser(this.props.loggedInUserId);
        }

        // if (!prevState.userDataLoaded && this.state.userDataLoaded) {
        //     const sharedWidgetParams = {
        //         userFirstName: this.props.loggedInUser.firstName,
        //         userLastName: this.props.loggedInUser.lastName,
        //         userEmail: this.props.loggedInUser.email,
        //         userProfileId: this.props.loggedInUserId,
        //         userCompany: this.props.userCompany,
        //         userTeams: this.props.activeUserTeams
        //     };
        //     if (window.beamer_config && window.Beamer) {
        //         initializeBeamer(sharedWidgetParams);
        //     }
        // }

        if (prevProps.authContext?.token !== authContext?.token) {
            setToken(authContext.token);
        }

        const companyPermalink = this.props.location.pathname.split('/')[2];
        if (
            this._isLoginLink() &&
            companyPermalink &&
            !companyPermalink.includes('error') &&
            isEmpty(this.props.currentLoginPageCompany) &&
            isEmpty(prevProps.currentLoginPageCompany)
        ) {
            this.props.getCompanyByPermalink(companyPermalink);
        }

        if (
            (!prevProps.userCompany || !prevProps.userCompany.whiteLabelColors) &&
            this.props.userCompany &&
            this.props.userCompany.whiteLabelColors
        ) {
            const {
                whiteLabelColors = {
                    navigationPrimaryBackground: '',
                    navigationPrimaryFont: '',
                    navigationSecondaryBackground: '',
                    navigationSecondaryFont: '',
                    primaryActionPrimaryBackground: '',
                    primaryActionPrimaryFont: '',
                    primaryActionSelectedBackground: '',
                    primaryActionSelectedFont: '',
                    secondaryActionPrimaryBackground: '',
                    secondaryActionPrimaryFont: '',
                    secondaryActionSelectedBackground: '',
                    secondaryActionSelectedFont: ''
                }
            } = this.props.userCompany;
            applyWhiteLabelStyle(whiteLabelColors);
        }
    }
    componentWillUnmount() {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    handleConfirmationModal = (val) => {
        this.setState({
            confirmationModalOpen: val
        })
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
      const { updatePreviousLocation, changeLanguage, isUpdatingWelcomeLanguage, isAuthenticated } = this.props;
      const { loggedInUserLanguage, displayLanguage } = nextProps;

          if (updatePreviousLocation && this.props.location.pathname !== nextProps.location.pathname) {
            updatePreviousLocation(this.props.location.pathname);
          }
          
          // Render in User's chosen language
          if (
            !isUpdatingWelcomeLanguage &&
            isAuthenticated &&
            loggedInUserLanguage &&
            loggedInUserLanguage !== SUPPORTED_LANGUAGES[displayLanguage].localeCode
          ) {
            changeLanguage(get(LANGUAGE_LOCALE_LOOKUP, `[${loggedInUserLanguage}].languageCode`, 'en'));
          }
        }
        
        handleBeforeUnload = (e) => {
          if (this.props.teamsUnsavedFlag) {
            e.preventDefault();
            e.returnValue = '';
          }
        };
        
        /**
         *
         * @param {string} loggedInUserId
         * @param {string} loggedInUserCompanyId
        */
       getLoggedInUserData = (loggedInUserId, loggedInUserCompanyId) => {
         const { getProfile, getCompany, getManyTeams, authContext, loginUserSuccess } = this.props;
         const userDataPromises = [];
         
         if (loggedInUserId) {
            userDataPromises.push(
                getProfile({
                    profileId: loggedInUserId,
                    isLoggedInUser: true,
                    pathsContext: this.context.pathsWeb
                })
            );
        } else {
            userDataPromises.push(Promise.resolve([]));
        }
        userDataPromises.push(getManyTeams({ pathsContext: this.context.pathsWeb }));

        userDataPromises.push(
            getCompany({
                pathsContext: this.context.pathsWeb,
                companyId: loggedInUserCompanyId,
                queryParams: { clientId: authContext.clientId }
            })
        );

        return BluebirdPromise.all(userDataPromises)
            .spread((userProfile) => {
                if (isEmpty(userProfile)) {
                    this.setState({ missingProfileError: true });
                } else {
                    loginUserSuccess({ userProfile, authContext });
                    this._identifySegmentUser(loggedInUserId);
                }
            })
            .finally(() => {
                this.setState({ userDataLoaded: true });
            });
    };

    _identifySegmentUser = (loggedInUserId) => {
        const { isVerbAdmin, userCompany = {} } = this.props;
        const currentDateTime = new Date();
        setLocalStorage({
            key: 'segmentLastIdentifyDate',
            value: currentDateTime,
            dispatchDisabled: true
        });
    };

    /**
     *
     * @return {boolean}
     *
     */
    _isLoginLink = () => this.props.location.pathname.match('/login') || this.props.location.pathname.match('/signup');

    _languageClass = () => {
        const { displayLanguage } = this.props;
        if (displayLanguage === 'ko') {
            return 'ko-app';
        }
        if (displayLanguage === 'pt') {
            return 'pt-app';
        }
        if (displayLanguage === 'es') {
            return 'es-app';
        }
        return null;
    };

    _canAccessAdminRoutes = () => {
        const {
            isAuthenticated,
            isVerbAdmin,
            isCompanyReportViewer,
            isContentReviewer,
            isContentAuthor,
            isContentAdmin,
            isAccountOwner,
            isVerbReportViewer,
            isInstructionalAdmin,
            isInstructionalContentAdmin,
            isInstructionalReportViewer
        } = this.props;

        return (
            isAuthenticated &&
            (isVerbAdmin ||
                isCompanyReportViewer ||
                isContentAuthor ||
                isContentAdmin ||
                isAccountOwner ||
                isVerbReportViewer ||
                isInstructionalAdmin ||
                isInstructionalContentAdmin ||
                isInstructionalReportViewer ||
                isContentReviewer)
        );
    };

    getNavigation = () => {
        const {
            isVerbAdmin,
            isAccountOwner,
            isContentReviewer,
            isContentAuthor,
            isContentAdmin,
            isCompanyReportViewer,
            isVerbReportViewer,
            isInstructionalAdmin,
            isInstructionalContentAdmin,
            isInstructionalReportViewer,
            loggedInUser,
            logout,
            badges,
            isAuthenticated,
            userCompany = {},
            location,
            activeUserTeams,
            history,
            teamsUnsavedFlag
        } = this.props;

        return (
            <Fragment>
                <Navigation
                    logout={logout}
                    isVerbAdmin={isVerbAdmin}
                    isContentAuthor={isContentAuthor}
                    isContentReviewer={isContentReviewer}
                    isAccountOwner={isAccountOwner}
                    isCompanyReportViewer={isCompanyReportViewer}
                    isContentAdmin={isContentAdmin}
                    isVerbReportViewer={isVerbReportViewer}
                    isInstructionalAdmin={isInstructionalAdmin}
                    isInstructionalContentAdmin={isInstructionalContentAdmin}
                    isInstructionalReportViewer={isInstructionalReportViewer}
                    loggedInUser={loggedInUser}
                    activeUserTeams={activeUserTeams}
                    userCompany={userCompany}
                    allBadges={badges}
                    isAuthenticated={isAuthenticated}
                    currentProfile={loggedInUser}
                    location={location}
                    changeLanguage={this.props.changeLanguage}
                    history={history}
                    teamsUnsavedFlag={teamsUnsavedFlag}
                    setUnsavedChanges={this.props.setUnsavedChanges}
                    handleConfirmationModal={this.handleConfirmationModal}
                />
                <MobileNavigation
                    logout={logout}
                    isVerbAdmin={isVerbAdmin}
                    isContentAuthor={isContentAuthor}
                    isContentReviewer={isContentReviewer}
                    isAccountOwner={isAccountOwner}
                    isCompanyReportViewer={isCompanyReportViewer}
                    isContentAdmin={isContentAdmin}
                    isVerbReportViewer={isVerbReportViewer}
                    isInstructionalAdmin={isInstructionalAdmin}
                    isInstructionalContentAdmin={isInstructionalContentAdmin}
                    isInstructionalReportViewer={isInstructionalReportViewer}
                    loggedInUser={loggedInUser}
                    activeUserTeams={activeUserTeams}
                    userCompany={userCompany}
                    allBadges={badges}
                    isAuthenticated={isAuthenticated}
                    currentProfile={loggedInUser}
                    location={location}
                    changeLanguage={this.props.changeLanguage}
                    history={history}
                    teamsUnsavedFlag={teamsUnsavedFlag}
                    setUnsavedChanges={this.props.setUnsavedChanges}
                    handleConfirmationModal={this.handleConfirmationModal}
                />
            </Fragment>
        );
    };

    // eslint-disable-next-line no-alert
    confirm = (message) => window.confirm(message || 'You have unsaved changes, are you sure you want to continue without saving changes?');

    showUnsavedChangesPrompt = () => {
        const confirmation = this.confirm();

        return !!confirmation;
    };


    handleNavigationBlock = () => {
        if (this.props.teamsUnsavedFlag) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
            
            if (confirmed) {
                // User confirmed - reset flag and allow navigation
                this.props.setUnsavedChanges(false);
                return true;
            } else {
                // User cancelled - keep the flag as is, just block navigation
                return false;
            }
        }
        return true; // Allow navigation
    };

    render() {
        const { loggedInUser, location, loggedInUserCompanyId, authContext, teamsUnsavedFlag } = this.props;

        const { userDataLoaded, confirmationModalOpen } = this.state;

        const mainClassName = cns('activePage');
        let navigationBarSection = this.getNavigation();

        if (
            location.pathname === '/welcome' ||
            location.pathname === '/auth/callback' ||
            location.pathname === '/legal' ||
            location.pathname === '/contentcatalog' ||
            location.pathname.startsWith('/notificationcallback') ||
            location.pathname.startsWith('/learningsampler')
        ) {
            navigationBarSection = null;
        }
        const canAccessAdminRoutes = this._canAccessAdminRoutes();

        const whiteLabelCompanyId = loggedInUser._id ? `co${loggedInUserCompanyId}` : '';

        return (
            <div
                className={cns('mainApp', 'redesign', whiteLabelCompanyId, this._languageClass(), {
                    admin: this.props.location.pathname.match('/admin/')
                })}
            >
                {isIE() ? <BrowserSupportWarning /> : null}
                <Suspense fallback={<FullPageLoadingIndicator />}>
                    <RootIntl store={this.props.store}>
                        {navigationBarSection}

                        <Modal modalType={this.props.modal.modalType} />
                        <Prompt
                            when={teamsUnsavedFlag && !confirmationModalOpen}
                            message={this.handleNavigationBlock}
                        />

                        <div className={mainClassName}>
                            <Switch>
                                {/* Home Routes */}
                                <Route
                                    path={'/home'}
                                    exact
                                    render={(props) => <Home pathsContext={this.context.pathsWeb} {...props} />}
                                />
                                <AuthenticatedRoute path={'/discover'} exact component={CustomDiscover} />
                                <AuthenticatedRoute path={'/home/:welcome'} exact component={Home} />
                                <Route
                                    path={'/pathway/:id'}
                                    exact
                                    render={(props) => <Home pathsContext={this.context.pathsWeb} {...props} />}
                                />
                                <Route
                                    path={'/'}
                                    exact
                                    render={() => (
                                        <Redirect to={this.props.isAuthenticated ? '/home' : '/auth/callback'} />
                                    )}
                                />
                                {/* Signon Routes */}
                                <Route exact path={'/silent'} component={SilentCallback} />
                                <Route exact path={'/contentcatalog'} component={ContentCatalog} />
                                <VerbRoute path={'/errorpage'} exact render={() => <GenericErrorPage />} />
                                <VerbRoute
                                    path={'/contentretirederrorpage'}
                                    exact
                                    render={() => <GenericErrorPage retiredContentError={true} />}
                                />
                                ÷
                                {userDataLoaded && <Route
                                    path={'/auth/callback'}
                                    authContext={authContext}
                                    exact
                                    render={(props) => (
                                        <AuthCallback authContext={authContext} {...props} nextPath={this.nextPath} />
                                    )}
                                />}
                                <Route path={'/notificationcallback'} exact component={NotificationCallback} />
                                {/* Collection Routes */}
                                <AuthenticatedRoute path={'/collection/:id'} exact component={CollectionPage} />
                                <AuthenticatedRoute
                                    path={'/collection/:id/:section'}
                                    exact
                                    component={CollectionPage}
                                />
                                {/* LearningAssets */}
                                <AuthenticatedRoute exact path={'/designpage'} component={DesignPage} />
                                <AuthenticatedRoute path={'/unsubscribe'} exact component={Unsubscribe} />
                                {/* Authenticated User Routes */}
                                <AuthenticatedRoute path={'/profile/:id/:section?'} exact component={Profile} />
                                {/* Explore */}
                                <Route path={'/explore'} exact component={Library} />{' '}
                                {/* Workspace and Interstitial Pages */}
                                <AuthenticatedRoute
                                    path={'/workspace/:userLearningAssetId'}
                                    exact
                                    component={Workspace}
                                />
                                <AuthenticatedRoute
                                    path={'/workspace/:userLearningAssetId/done'}
                                    exact
                                    component={LearningAssetInterstitial}
                                />
                                <AuthenticatedRoute
                                    path={'/workspace/:userLearningAssetId/take-action-done'}
                                    exact
                                    component={TakeActionActivityInterstitial}
                                />
                                <AuthenticatedRoute
                                    path={'/mycollection/:userCollectionId/done'}
                                    exact
                                    component={CollectionInterstitial}
                                />
                                <AuthenticatedRoute
                                    path={'/mypathway/:userPathwayId/done'}
                                    exact
                                    component={PathwayInterstitial}
                                />
                                <Route path={'/learningsampler/:id'} exact component={LearningSampler} />
                                <AuthenticatedRoute
                                    path={'/assetlink/:learningAssetId'}
                                    exact
                                    customProps={{ userDataLoaded: this.state.userDataLoaded }}
                                    component={AssetLink}
                                />
                                <AuthenticatedRoute
                                    path={'/directlink/:collectionId/:companyId'}
                                    exact
                                    customProps={{ userDataLoaded: this.state.userDataLoaded }}
                                    component={DirectLink}
                                />
                                {/* Callback for weekly digest */}
                                <AuthenticatedRoute path={'/digestcallback'} exact component={DigestCallback} />
                                {/* Teams/Groups Route */}
                                <AuthenticatedRoute path={'/teams/:id/:section'} exact component={PathsTeamsContainerV2} />
                                <AuthenticatedRoute path={'/teams/:id'} exact component={PathsTeamsContainerV2} />
                                <AuthenticatedRoute path={'/grouplist'} exact component={GroupList} />
                                {/* Framework Routes */}
                                <AuthenticatedRoute path={'/dimension/:id'} exact component={DimensionPage} />
                                {/* Not Found Route */}
                                <VerbRoute path={'/404'} exact component={NotFound} />
                                {/* Admin Routes */}
                                <Admin {...this.props} teamsUnsavedFlag={teamsUnsavedFlag} />
                                {/* Catch All Route */}
                                <Route
                                    render={() =>
                                        this.props.isAuthenticated && !canAccessAdminRoutes ? (
                                            <NotFound userDataLoaded={userDataLoaded} />
                                        ) : (
                                            <Redirect to={'/errorpage'} />
                                        )
                                    }
                                />
                            </Switch>
                        </div>
                    </RootIntl>
                </Suspense>
            </div>
        );
    }
}

export const mapStateToProps = (state) => {
    const {
        auth: {
            isAuthenticated,
            isVerbAdmin,
            isAccountOwner,
            loggedInUserId,
            loggedInUser,
            loggedInUserCompanyId,
            isContentReviewer,
            isContentAuthor,
            isContentAdmin,
            isCompanyReportViewer,
            companyPermalink,
            loggedInUserLanguage,
            isVerbReportViewer,
            isInstructionalAdmin,
            isInstructionalContentAdmin,
            isInstructionalReportViewer,
        },
        companies,
        badges,
        modal,
        nav,
        mobile,
        language,
        teams,
        welcome: { returnToInfoSlide },
        profiles
    } = state;

    const userProfile = profiles.byId;

    const loginPageCompanyPermalink = window.location.pathname.split('/')[2] || null;
    const currentLoginPageCompany = (companies.byPermalink && companies.byPermalink[loginPageCompanyPermalink]) || {};
    const activeUserTeams = [];

    if (loggedInUserId in userProfile) {
        (userProfile[loggedInUserId].teams || []).forEach((team) => {
            if (teams.byId && teams.byId[team.teamId] && teams.byId[team.teamId].isActive) {
                activeUserTeams.push({ ...teams.byId[team.teamId], joinedDateTime: team.joinedDateTime });
            }
        });
    }

    const { languageLoaded } = state.language;
    const { teamsUnsavedFlag } = state.teams;
    return {
        activeUserTeams,
        shouldExpandNavigation: nav.expandNavigation,
        isAuthenticated,
        isVerbAdmin,
        isContentReviewer,
        isContentAuthor,
        isAccountOwner,
        isContentAdmin,
        isCompanyReportViewer,
        isVerbReportViewer,
        isInstructionalAdmin,
        isInstructionalContentAdmin,
        isInstructionalReportViewer,
        loggedInUserCompanyId,
        userCompany: companies.byId[loggedInUserCompanyId] || {},
        currentLoginPageCompany,
        companyPermalink,
        loggedInUser,
        loggedInUserId,
        loggedInUserLanguage,
        badges: badges.byId,
        modal,
        mobile,
        displayLanguage: language.languageCode || 'en',
        isUpdatingWelcomeLanguage: returnToInfoSlide,
        languageLoaded,
        teamsUnsavedFlag
    };
};

export const mapDispatchToProps = (dispatch) => ({
    showModal: (params) => dispatch(showModal(params)),
    isMobile: () => dispatch(isMobile()),
    getProfile: (params) => dispatch(getProfile(params)),
    setToken: (params) => dispatch(setToken(params)),
    getCompany: (params) => dispatch(getCompany(params)),
    getCompanyByPermalink: (params) => dispatch(getCompanyByPermalink(params)),
    getManyBadges: (params) => dispatch(getManyBadges(params)),
    collapseNavigation: () => dispatch(collapseNavigation()),
    expandNavigation: () => dispatch(expandNavigation()),
    updatePreviousLocation: (prevLocation) => dispatch(updatePreviousLocation(prevLocation)),
    changeLanguage: (params) => dispatch(changeLanguage(params)),
    getManySettings: (params) => dispatch(getManySettings(params)),
    getManyTeams: (params) => dispatch(getManyTeams(params)),
    loginUserSuccess: (params) => dispatch(loginUserSuccess(params)),
    authenticateLoggedInUserOnly: () => dispatch(authenticateLoggedInUserOnly()),
    setUnsavedChanges: (params) => dispatch(setUnsavedChanges(params))
});

export const AppComponent = App;
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
